import React, { useState } from "react";
import { Helmet } from 'react-helmet-async'
import algoliasearch from 'algoliasearch/lite'
import { 
    InstantSearch, 
    SearchBox, 
    connectHits, 
    RefinementList, 
    Panel, 
    NumericMenu, 
    SortBy, 
    connectStateResults
} from 'react-instantsearch-dom'
import { Algolia, BASE_URL, IMG } from '../../constant'
import '../../sass/mobilesrc.scss'
import { Link } from "react-router-dom";

// Initialize Algolia Search Client
const searchClient = algoliasearch(Algolia.ApplicationID, Algolia.SearchOnlyAPIKey);

// Calculate Discount Percentage
const getDiscountPercentage = (regularPrice, salePrice) => {
    if (regularPrice && salePrice && regularPrice > salePrice) {
        return ((regularPrice - salePrice) / regularPrice) * 100;
    }
    return 0;
};

// Get Lowest Sale Price from Variants
const getLowestSalePrice = (pro_var) => {
    if (pro_var && pro_var.length > 0) {
        const prices = pro_var.map((variant) => parseFloat(variant.sale_price || 0));
        return Math.min(...prices);
    }
    return null;
};

// Product Card Component
const RenderItem = ({ hit }) => {
    const discount = getDiscountPercentage(hit.regular_price, hit.sale_price);
    const lowestPrice = getLowestSalePrice(hit.ProVar);

    return (
        <div className={`product-card ${hit.stock_status === 'Out of stock' ? 'out-of-stock' : ''}`}>
            <Link to={`/product/${hit.slug}`}>
                <img src={hit.thumbnail ? BASE_URL + hit.thumbnail : "https://via.placeholder.com/150"} alt={hit.title} className="product-image" />
                <h4 className="product-title">{hit.title}</h4>
                {lowestPrice !== null ? (
                    <div className="product-price">
                        <span className="price_regular">Starting @ <span className="price_sale">₹{lowestPrice}</span></span>
                    </div>
                ) : hit.sale_price ? (
                    <>
                        <div className="product-price">
                            <div className="priceRow">
                                <span className="price_regular">MRP </span> <span className="price_regular strike">₹{hit.regular_price}</span>
                            </div>
                            <div className="priceRow">
                                <span className="price_sale">₹{hit.sale_price}</span> <span className="price_discount">({discount.toFixed(0)}% OFF)</span>
                            </div>
                        </div>
                        <div className="discount-badge-wrapper">
                            <img src={IMG.tag} className="discount-badge" alt="discount-badge" />
                            <div className="discount-badge-text">{discount.toFixed(0)}% OFF</div>
                        </div>
                    </>
                ) : (
                    <div className="product-price">
                        <span className="price_regular">₹{hit.regular_price}</span>
                    </div>
                )}
                {/* <p className="stock-status">{hit.stock_status === "Out of stock" ? "Out of Stock" : "In Stock"}</p> */}
            </Link>
        </div>
    );
};

// Custom Hits Component (Product Listing)
const CustomHits = connectHits(({ hits }) => (
    <>
        {hits.length > 0 ?
            <div className="products-grid">
                {hits.map(hit => (
                    <RenderItem key={hit.objectID} hit={hit} />
                ))}
            </div>
        : <h6>Sorry, we couldn't find any results.</h6>}
    </>
));

// Custom Stats Component (Always Show Results Count)
const CustomStats = connectStateResults(({ searchResults, searching }) => {
    if (!searchResults || searching) return null;

    const { nbHits, query } = searchResults; // processingTimeMS,
    return (
        <div className="results-info">
            {query ? (
                <p className="results-info-count">Showing <b>{nbHits}</b> results for <b>"{query}"</b></p> // in {processingTimeMS}ms
            ) : (
                // <p>Showing {nbHits} products available</p>
                <p></p>
            )}
        </div>
    );
});

// Main Search Component
const SearchSmall = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleToggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleCloseSidebar = () => {
        setIsSidebarOpen(false);
    };

    return (
        <>
            <Helmet>
                <title>Search Dental Products Online | DentalStores.in</title>
                <meta name="description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="keywords" content="dental product search, buy dental instruments, best dental supplies, dental materials online, dental e-commerce" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.dentalstores.in/search" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.dentalstores.in/search" />
                <meta property="og:title" content="Search Dental Products Online | DentalStores.in" />
                <meta property="og:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta property="og:image" content="https://www.dentalstores.in/logo.svg" />

                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Search Dental Products Online | DentalStores.in" />
                <meta name="twitter:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="twitter:image" content="https://www.dentalstores.in/logo.svg" />
            </Helmet>
            <div className="search-container">
                <InstantSearch indexName={Algolia.IndexName} searchClient={searchClient}>
                    
                    {/* Search Box */}
                    <SearchBox className="search-input" translations={{ placeholder: "Search for products, brands..." }} />
                    

                    {/* Results Count */}
                    <CustomStats />
                    <div className="first-row">

                        <div className="filters-btn" onClick={handleToggleSidebar}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" class="sc-8235fce6-13 eQmbcY"><path stroke="#1D2B36" stroke-linecap="round" stroke-width="1.5" d="M4.5 1.5v2.25M13.5 13.5v3"></path><path fill="#1D2B36" d="M5.25 11.25v-.75h-1.5v.75h1.5Zm-1.5 5.25a.75.75 0 0 0 1.5 0h-1.5Zm0-5.25v5.25h1.5v-5.25h-1.5ZM14.25 1.5a.75.75 0 0 0-1.5 0h1.5ZM12.75 6v.75h1.5V6h-1.5Zm0-4.5V6h1.5V1.5h-1.5Z"></path><path fill="#1D2B36" fill-rule="evenodd" d="M7.5 6a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm12 6.75a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" clip-rule="evenodd"></path></svg>
                            Filters
                        </div>

                        <SortBy
                            defaultRefinement={Algolia.IndexName}
                            items={[
                                { value:  Algolia.IndexName, label: "Sort by" },
                                { value: Algolia.IndexName, label: "Relevance" },
                                { value: `${Algolia.IndexName}_sale_price_asc`, label: "Price: Low to High" },
                                { value: `${Algolia.IndexName}_sale_price_desc`, label: "Price: High to Low" }
                            ]}
                            onChange={(event) => console.log("Sort changed:", event.target.value)}
                        />
                    </div>

                    <div className={`filters-sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}>
                        <div className="filters-sidebar-heading">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" class="sc-8235fce6-13 eQmbcY"><path stroke="#1D2B36" stroke-linecap="round" stroke-width="1.5" d="M4.5 1.5v2.25M13.5 13.5v3"></path><path fill="#1D2B36" d="M5.25 11.25v-.75h-1.5v.75h1.5Zm-1.5 5.25a.75.75 0 0 0 1.5 0h-1.5Zm0-5.25v5.25h1.5v-5.25h-1.5ZM14.25 1.5a.75.75 0 0 0-1.5 0h1.5ZM12.75 6v.75h1.5V6h-1.5Zm0-4.5V6h1.5V1.5h-1.5Z"></path><path fill="#1D2B36" fill-rule="evenodd" d="M7.5 6a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm12 6.75a3 3 0 1 0-6 0 3 3 0 0 0 6 0Zm-3-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" clip-rule="evenodd"></path></svg>
                            Filters
                        </div>
                        <div className="filters-container">
                            {/* Stock Status Filter */}
                            <Panel header="Status">
                                <RefinementList attribute="stock_status" className="custom-refinement-list" />
                            </Panel>

                            {/* Categories Filter (With Search) */}
                            <Panel header="Categories">
                                <RefinementList 
                                    attribute="categories.name" 
                                    searchable={true} 
                                    translations={{ placeholder: "Search categories..." }}
                                    className="custom-refinement-list"
                                />
                            </Panel>

                            {/* Brands Filter (With Search & Show More) */}
                            <Panel header="Brands">
                                <RefinementList 
                                    attribute="brand"
                                    limit={5} 
                                    // showMore={true} 
                                    // showMoreLimit={10} 
                                    searchable={true} 
                                    translations={{ placeholder: "Search brands..." }}
                                    className="custom-refinement-list"
                                />
                            </Panel>

                            {/* Price Range Filter */}
                            <Panel header="Price Range">
                                <NumericMenu 
                                    attribute="sale_price"
                                    items={[
                                        { label: "Under ₹500", end: 500 },
                                        { label: "₹500 - ₹1000", start: 500, end: 1000 },
                                        { label: "₹1000 - ₹5000", start: 1000, end: 5000 },
                                        { label: "₹5000 - ₹10000", start: 5000, end: 10000 },
                                        { label: "Above ₹10000", start: 10000 }
                                    ]}
                                    className="custom-price-range"
                                />
                            </Panel>
                        </div>
                    </div>
                    {isSidebarOpen && (
                        <div className="filters-sidebar-backdrop" onClick={handleCloseSidebar}></div>
                    )}
                    <div className='product_wrapper'>
                        {/* Product Listing */}
                        <CustomHits />
                    </div>

                </InstantSearch>
            </div>
        </>
    );
};

export default SearchSmall;
